import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Button from "@/components/base/Button";

export default function CTA() {
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [form, setForm] = useState({
    name: "",
    firma: "",
    email: "",
    phone: "",
    address: "",
    project: "Telefonzentrale",
    timeline: "Innerhalb eines Monats",
    notes: "",
  });

  const update = (k: keyof typeof form, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const resetForm = () =>
    setForm({
      name: "",
      firma: "",
      email: "",
      phone: "",
      address: "",
      project: "Telefonzentrale",
      timeline: "Innerhalb eines Monats",
      notes: "",
    });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const data = new FormData(formEl);

    const honeypot = ((data.get("company_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("ok");
      resetForm();
      setTimeout(() => setStatus("idle"), 5000);
      return;
    }

    if (!form.name || !form.email) return;
    setStatus("loading");

    const body = new URLSearchParams();
    data.forEach((v, k) => {
      if (k === "company_alt") return;
      body.append(k, v.toString());
    });

    // Erst ins Formular-Archiv.
    let archived = false;
    try {
      const res = await fetch("https://readdy.ai/api/form/dakpkqsov550s8tcof0g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      archived = res.ok && parsed.code === "OK";
    } catch {
      archived = false;
    }

    // Zusätzlich Bestätigungs- und Info-Mail - auch dann, wenn das Archiv klemmt.
    let mailed = false;
    try {
      const { data: mailData, error } = await supabase.functions.invoke(
        "send-inquiry-email",
        {
          body: {
            name: form.name,
            firma: form.firma,
            email: form.email,
            phone: form.phone,
            address: form.address,
            project: form.project,
            timeline: form.timeline,
            notes: form.notes,
          },
        }
      );
      if (!error && (mailData as { success?: boolean } | null)?.success) {
        mailed = true;
      }
    } catch {
      mailed = false;
    }

    if (archived || mailed) {
      setStatus("ok");
      resetForm();
      setTimeout(() => setStatus("idle"), 5000);
    } else {
      setStatus("err");
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  return (
    <section
      id="quote"
      data-stage="5"
      className="relative py-32 md:py-48 px-6 md:px-12 overflow-hidden"
    >
      <div className="relative max-w-3xl mx-auto">
        <div className="text-center mb-14">
          <span
            data-reveal
            className="inline-block text-xs tracking-[0.4em] uppercase text-moss mb-5"
          >
            · EINE LETZTE FRAGE.
          </span>
          <h2
            data-reveal
            data-delay="1"
            className="font-display text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] text-ink"
          >
            <div>Würde morgen jemand</div>
            <div>ans Telefon gehen?</div>
          </h2>
          <p
            data-reveal
            data-delay="2"
            className="mt-8 text-base md:text-lg text-ink max-w-lg mx-auto leading-relaxed"
          >
            Wenn plötzlich zehn neue Interessenten anrufen würden: Wäre jemand
            erreichbar? Falls nicht, sollten wir miteinander sprechen.
          </p>
        </div>

        <form
          data-reveal
          data-delay="3"
          data-readdy-form
          id="quote-form"
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 md:p-10 rounded-3xl border border-line bg-bg/70 backdrop-blur-xl"
        >
          <input
            type="text"
            name="company_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="offscreen-field"
          />
          <input type="hidden" name="timeline" value={form.timeline} />
          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-[0.2em] text-inkDim mb-2">
              Vollständiger Name
            </label>
            <input
              required
              type="text"
              name="name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              autoComplete="name"
              className="w-full bg-bgSoft border border-line rounded-lg px-4 py-3 text-sm text-ink focus:outline-none focus:border-sage transition-colors"
              placeholder="Vor- und Nachname"
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-[0.2em] text-inkDim mb-2">
              Firma
            </label>
            <input
              type="text"
              name="firma"
              value={form.firma}
              onChange={(e) => update("firma", e.target.value)}
              autoComplete="organization"
              className="w-full bg-bgSoft border border-line rounded-lg px-4 py-3 text-sm text-ink focus:outline-none focus:border-sage transition-colors"
              placeholder="Firmenname"
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-[0.2em] text-inkDim mb-2">
              E-Mail
            </label>
            <input
              required
              type="email"
              name="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              autoComplete="email"
              className="w-full bg-bgSoft border border-line rounded-lg px-4 py-3 text-sm text-ink focus:outline-none focus:border-sage transition-colors"
              placeholder="ihre@firma.de"
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-[0.2em] text-inkDim mb-2">
              Telefon
            </label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              autoComplete="tel"
              className="w-full bg-bgSoft border border-line rounded-lg px-4 py-3 text-sm text-ink focus:outline-none focus:border-sage transition-colors"
              placeholder="+49 …"
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-[0.2em] text-inkDim mb-2">
              Adresse
            </label>
            <input
              type="text"
              name="address"
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              autoComplete="street-address"
              className="w-full bg-bgSoft border border-line rounded-lg px-4 py-3 text-sm text-ink focus:outline-none focus:border-sage transition-colors"
              placeholder="Straße, PLZ, Ort"
            />
          </div>

          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-[0.2em] text-inkDim mb-2">
              Wobei dürfen wir unterstützen?
            </label>
            <div className="relative">
              <select
                name="project"
                value={form.project}
                onChange={(e) => update("project", e.target.value)}
                className="w-full appearance-none bg-bgSoft border border-line rounded-lg px-4 py-3 pr-10 text-sm text-ink focus:outline-none focus:border-sage transition-colors cursor-pointer"
              >
                <option>Telefonzentrale</option>
                <option>Kundenservice</option>
                <option>Terminvereinbarung</option>
                <option>Leadgenerierung</option>
                <option>KI + Mensch</option>
                <option>Fulfillment</option>
                <option>Sonstiges</option>
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-inkDim pointer-events-none"></i>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-[0.2em] text-inkDim mb-2">
              Wann möchten Sie starten?
            </label>
            <div className="flex flex-wrap gap-2">
              {["Diese Woche", "Innerhalb eines Monats", "Diese Saison", "Nur Planung"].map(
                (t) => (
                  <Button
                    key={t}
                    size="sm"
                    variant="outline"
                    onClick={() => update("timeline", t)}
                    className={
                      form.timeline === t
                        ? "bg-sage text-bg border-sage"
                        : "border-line text-inkDim hover:border-sage hover:text-ink"
                    }
                  >
                    {t}
                  </Button>
                )
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-[0.2em] text-inkDim mb-2">
              Erzählen Sie uns von sich und Ihren Ideen
            </label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={(e) =>
                update("notes", e.target.value.slice(0, 500))
              }
              maxLength={500}
              rows={4}
              className="w-full bg-bgSoft border border-line rounded-lg px-4 py-3 text-sm text-ink focus:outline-none focus:border-sage transition-colors resize-none"
              placeholder="Wachstum. Durchschlafen. Wachstum. Wachstum. Glückseligkeit."
            />
            <div className="mt-1 text-right text-[10px] text-inkDim">
              {form.notes.length}/500
            </div>
          </div>

          <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
            <div className="space-y-1.5">
              <p className="text-xs text-inkDim/80 flex items-center gap-2">
                <i className="ri-shield-check-line text-moss"></i>
                Wir geben Ihre Daten niemals weiter. Versprochen.
              </p>
              <p className="text-[11px] leading-relaxed text-inkDim/70">
                Mit dem Absenden bestätigen Sie, dass Sie unsere{" "}
                <Link
                  to="/datenschutz"
                  className="underline decoration-sage/60 underline-offset-2 hover:text-ink transition-colors cursor-pointer"
                >
                  Datenschutzerklärung
                </Link>{" "}
                zur Kenntnis genommen haben.
              </p>
            </div>
            <Button
              type="submit"
              size="lg"
              disabled={status === "loading"}
              className="w-full sm:w-auto bg-sage text-bg hover:bg-ink"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> Wird gesendet …
                </>
              ) : status === "ok" ? (
                <>
                  <i className="ri-check-line"></i> Danke - wir melden uns
                </>
              ) : status === "err" ? (
                <>
                  <i className="ri-error-warning-line"></i> Erneut versuchen
                </>
              ) : (
                <>
                  <span className="sm:hidden">Angebot anfordern</span>
                  <span className="hidden sm:inline">Kostenloses Angebot anfordern</span>{" "}
                  <i className="ri-arrow-right-line"></i>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}